import React from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "MainLayout";

import FrontPage from "components/pages/frontPage/FrontPage.js";

import PhysiciansListComponent from "components/pages/physicians/PhysiciansList/PhysiciansListComponent";
import PhysiciansEditContainer from "components/pages/physicians/PhysiciansEdit/PhysiciansEditContainer";
import PhysiciansShowContainer from "components/pages/physicians/PhysiciansShow/PhysiciansShowContainer";
import PhysiciansCreateContainer from "components/pages/physicians/PhysiciansCreate/PhysiciansCreateContainer";

import CollaboratorsList from "components/pages/collaborators/CollaboratorsList/CollaboratorsList";
import CollaboratorCreate from "components/pages/collaborators/CollaboratorCreate/CollaboratorCreate";
import CollaboratorsShow from "components/pages/collaborators/CollaboratorsShow/CollaboratorsShow";
import CollaboratorEdit from "components/pages/collaborators/CollaboratorEdit/CollaboratorEdit";

import ClientsListContainer from "components/pages/clients/ClientsList/ClientsListComponent";
import ClientsShowContainer from "components/pages/clients/ClientsShow/ClientsShowContainer";
import ClientCreateContainer from "components/pages/clients/ClientCreate/ClientCreateContainer";
import ClientEditContainer from "components/pages/clients/ClientEdit/ClientEditContainer";

import UsersListContainer from "components/pages/users/UsersList/UsersListComponent";
import UserCreateContainer from "components/pages/users/UserCreate/UserCreateContainer.js";
import UserShowContainer from "components/pages/users/UserShow/UserShowContainer";
import UserEditContainer from "components/pages/users/UserEdit/UserEditContainer";

import UserProfileShow from "components/pages/userProfile/UserProfileShow/UserProfileShow";
import UserProfileEdit from "components/pages/userProfile/UserProfileEdit/UserProfileEdit";

import SalesListContainer from "components/pages/sales/SalesList/SalesListComponent";
import SalesShowContainer from "components/pages/sales/SalesShow/SalesShowContainer";
import SalesCreateContainer from "components/pages/sales/SalesCreate/SalesCreateContainer";
import SalesEditContainer from "components/pages/sales/SalesEdit/SalesEditContainer";

import RequisitionsListComponent from "components/pages/requisitions/RequisitionsList/RequisitionsListComponent";
import RequisitionsShowComponent from "components/pages/requisitions/RequisitionsShow/RequisitionsShowContainer";
import RequisitionsCreateComponent from "components/pages/requisitions/RequisitionsCreate/RequisitionsCreateContainer";
// import RequisitionsCreateComponent from "components/pages/requisitions/RequisitionsCreateNew/RequisitionsCreateContainer";

import LoginPage from "components/pages/login/LoginContainer.js";
import ResetPasswordPage from "components/pages/resetPassword/ResetPasswordContainer";

import EditTrfContainer from "components/pages/requisitions/RequisitionsCreateNew/EditTrfContainer";

import FreezerReverseLookup from "components/pages/freezerReverseLookup/FreezerReverseLookup.js";

import { userRoles, userGroups, userPermissions } from "config/userRoles";

import SmartRoute from "SmartRoute";
import GuestRoute from "GuestRoute";
import Reports from "components/pages/reports/Reports";
import TestReportsUpload from "components/pages/testReportsUpload/TestReportsUploadContainer";

import BatchCsvUpload from "components/pages/batchCsvUpload/BatchCsvUpload.js";

import authStorage from "providers/authStorage.js";
import Error404 from "components/pages/errors/Error404";
import Error403 from "components/pages/errors/Error403";

import SubscribePage from "components/pages/subscribe/SubscribePageContainer";

// Wrapper components for dynamic routes
const UserProfileShowWrapper = () => {
  const access = authStorage.getAccess();
  return <UserProfileShow resource="users" id={access?.userId} />;
};

const UserProfileEditWrapper = () => {
  const access = authStorage.getAccess();
  return <UserProfileEdit resource="users" id={access?.userId} />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<GuestRoute element={<LoginPage />} />} />

    <Route path="/reset/:token" element={<GuestRoute element={<ResetPasswordPage />} />} />

    <Route
      path="/unsubscribe/:token"
      element={
        <GuestRoute
          allowRegistered
          element={<SubscribePage type="unsubscribe" />}
        />
      }
    />
    <Route
      path="/resubscribe/:token"
      element={
        <GuestRoute
          allowRegistered
          element={<SubscribePage type="resubscribe" />}
        />
      }
    />

    <Route
      path="/"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<FrontPage />}
          accessRoles={userGroups.allRegistered}
        />
      }
    />

    <Route
      path="/collaborators/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<CollaboratorCreate resource="collaborators" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route
      path="/freezer-specs"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<FreezerReverseLookup resource="freezer_specs" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route
      path="/collaborators/:id"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<CollaboratorEdit resource="collaborators" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/collaborators/:id/show"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<CollaboratorsShow resource="collaborators" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route
      path="/collaborators"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<CollaboratorsList resource="collaborators" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route
      path="/clients/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<ClientCreateContainer resource="clients" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/clients/:id"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<ClientEditContainer resource="clients" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/clients/:id/show"
      element={
        <SmartRoute
          layout={MainLayout}
          layoutProps={{ showFooter: true }}
          element={<ClientsShowContainer resource="clients" />}
          accessRoles={[...userGroups.superAdmins, userRoles.sales]}
        />
      }
    />
    <Route
      path="/clients"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<ClientsListContainer resource="clients" />}
          accessRoles={[...userGroups.superAdmins, userRoles.sales]}
        />
      }
    />

    <Route
      path="/physicians/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<PhysiciansCreateContainer resource="physicians" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/physicians/:id"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<PhysiciansEditContainer resource="physicians" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/physicians/:id/show"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<PhysiciansShowContainer resource="physicians" />}
          accessRoles={[...userGroups.superAdmins, userRoles.sales]}
        />
      }
    />
    <Route
      path="/physicians"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<PhysiciansListComponent resource="physicians" />}
          accessRoles={[...userGroups.superAdmins, userRoles.sales]}
        />
      }
    />

    <Route
      path="/sales/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<SalesCreateContainer resource="sales" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/sales/:id"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<SalesEditContainer resource="sales" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/sales/:id/show"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<SalesShowContainer resource="sales" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />
    <Route
      path="/sales"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<SalesListContainer resource="sales" />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route
      path="/requisitions/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<RequisitionsCreateComponent />}
          accessRoles={[
            userRoles.admin,
            userRoles.collaborator,
            userRoles.management,
            userRoles.lisstaff,
          ]}
        />
      }
    />
    <Route
      path="/requisitions"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<RequisitionsListComponent />}
          accessRoles={userGroups.allRegistered}
        />
      }
    />
    <Route
      path="/edit-trf/:reqId"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<EditTrfContainer />}
          accessRoles={[
            userRoles.admin,
            userRoles.management,
            userRoles.lisstaff,
          ]}
        />
      }
    />
    <Route
      path="/requisitions/:id/show/:tabSlug?"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<RequisitionsShowComponent />}
          accessRoles={[
            userRoles.admin,
            userRoles.collaborator,
            userRoles.management,
            userRoles.lisstaff,
            userRoles.sales,
            userRoles.billing,
          ]}
        />
      }
    />

    <Route
      path="/users/create"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UserCreateContainer resource="users" />}
          accessRoles={userPermissions.userEntity.resource}
        />
      }
    />
    <Route
      path="/users/:id"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UserEditContainer resource="users" />}
          accessRoles={userPermissions.userEntity.resource}
        />
      }
    />
    <Route
      path="/users/:id/show"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UserShowContainer resource="users" />}
          accessRoles={userPermissions.userEntity.resource}
        />
      }
    />
    <Route
      path="/users"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UsersListContainer resource="users" />}
          accessRoles={userPermissions.userEntity.resource}
        />
      }
    />

    <Route
      path="/user-profile"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UserProfileShowWrapper />}
          accessRoles={userPermissions.userInfo.resource}
        />
      }
    />
    <Route
      path="/user-profile/edit"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<UserProfileEditWrapper />}
          accessRoles={userPermissions.userInfo.resource}
        />
      }
    />

    <Route
      path="/reports/:tabSlug?"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<Reports />}
          accessRoles={userGroups.allRegistered}
        />
      }
    />

    <Route
      path="/test-reports-upload"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<TestReportsUpload />}
          accessRoles={userGroups.allRegistered}
        />
      }
    />

    <Route
      path="/batch-csv-upload"
      element={
        <SmartRoute
          layout={MainLayout}
          element={<BatchCsvUpload />}
          accessRoles={userGroups.superAdmins}
        />
      }
    />

    <Route path="/403" element={<SmartRoute layout={MainLayout} element={<Error403 />} />} />
    <Route path="*" element={<SmartRoute layout={MainLayout} element={<Error404 />} />} />
  </Routes>
);

export default AppRoutes;
